import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/customers/{id}/password';
const HTTP_METHOD = 'put';

export const UpdatePasswordError = createBackendApiError({
  domain: 'Customer',
  endpoint: 'UpdatePassword',
  path: ENDPOINT,
  identifierField: 'customerId',
} as const);

export type UpdatePasswordError = BackendApiErrorUnion<typeof UpdatePasswordError>;

export type CustomerUpdatePasswordPayload = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

const putPasswordFetch = (id: string, data: CustomerUpdatePasswordPayload) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json', 'application/json')
      .fetch({ params: { id }, body: data });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'customer.id': id,
      },
    })
  );

export const updatePassword = (id: string, data: CustomerUpdatePasswordPayload) =>
  putPasswordFetch(id, data).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': () => Effect.void,
        404: (response) =>
          Effect.fail(new UpdatePasswordError.NotFoundError({ customerId: id, response })),
        422: UpdatePasswordError.createViolationError(id),
        orElse: (response) =>
          Effect.fail(new UpdatePasswordError.ApiRequestError({ customerId: id, response })),
      })
    ),
    Effect.tap(() =>
      logApiOperation(
        ApiModule.CustomerPasswordUpdate,
        'Debug',
        `Successfully updated password for customer ${id}`
      )
    ),
    Effect.withSpan('api.customer.updatePassword', {
      kind: 'internal',
      attributes: {
        'customer.id': id,
      },
    })
  );
